// src/services/cardRegistry.js
import logger from '../utils/logger';

export class CardRegistry {
  constructor() {
    this.cards = {};
    this.cardMetadata = {}; // Store card metadata (category, priority, etc.)
    this.roleCards = {};
    this.initialized = false;
  }

  /**
   * Check if registry has been initialized
   */
  isInitialized() {
    return this.initialized;
  }

  /**
   * Initialize registry with default cards
   */
  initialize() {
    // Set initialized flag to true
    this.initialized = true;
    logger.log('Card registry initialized');
    return this;
  }

  /**
   * Set initialized flag for registry
   */
  setInitialized() {
    this.initialized = true;
    logger.log('Card registry marked as initialized');
    return this;
  }

  /**
   * Register a card with the registry
   * @param {Object} card - Card definition
   * @param {Object} options - Card options (roles, category, priority)
   */
  registerCard(card, options = {}) {
    if (!card.id) {
      console.error('Card must have an ID to be registered:', card);
      return;
    }

    // Standardize size properties with sensible defaults
    const enhancedCard = {
      ...card,
      width: card.width || 6,          // Default to half screen width
      height: card.height || 6,        // Default height (300px with 50px row height)
      minWidth: card.minWidth || 3,    // Minimum width
      minHeight: card.minHeight || 4,  // Minimum height (200px) - fits most card content
      responsive: card.responsive || {
        sm: { width: Math.min(card.width || 6, 6) },  // Small screens
        xs: { width: 4 }                             // Extra small screens
      }
    };

    this.cards[card.id] = enhancedCard;
    
    // Store metadata separately
    this.cardMetadata[card.id] = {
      category: options.category || 'general',
      priority: options.priority || 0,
      roles: options.roles || [],
      ...options // Store any additional metadata
    };
    

    // If options includes roles, register card for those roles
    if (options && options.roles && Array.isArray(options.roles)) {
      options.roles.forEach(role => {
        if (!this.roleCards[role]) {
          this.roleCards[role] = [];
        }
        if (!this.roleCards[role].includes(card.id)) {
          this.roleCards[role].push(card.id);
        }
      });
    }
  }

  /**
   * Register multiple cards at once
   * @param {Array} cards - Array of card definitions
   */
  registerCards(cards) {
    if (!Array.isArray(cards)) {
      console.error('registerCards expects an array of cards');
      return;
    }
    
    cards.forEach(card => this.registerCard(card));
  }

  /**
   * Get a card by ID
   * @param {string} id - Card ID
   * @returns {Object} Card definition
   */
  getCard(id) {
    return this.cards[id];
  }

  /**
   * Get card metadata by ID
   * @param {string} id - Card ID
   * @returns {Object} Card metadata (category, priority, roles, etc.)
   */
  getCardMetadata(id) {
    return this.cardMetadata[id];
  }

  /**
   * Get all registered cards
   * @returns {Object} All card definitions
   */
  getAllCards() {
    return Object.values(this.cards);
  }

  /**
   * Associate cards with specific user roles
   * @param {string} role - User role
   * @param {Array} cardIds - Array of card IDs
   */
  setCardsForRole(role, cardIds) {
    if (!Array.isArray(cardIds)) {
      console.error('cardIds must be an array');
      return;
    }
    
    this.roleCards[role] = cardIds;
    logger.log(`Set ${cardIds.length} cards for role: ${role}`);
  }

  /**
   * Get all cards for a specific user role
   * @param {string} role - User role
   * @returns {Array} Array of card definitions for the role
   */
  getCardsForRole(role) {
    const cardIds = this.roleCards[role] || [];
    
    // Map card IDs to card definitions
    const cards = cardIds
      .map(id => this.getCard(id))
      .filter(card => card !== undefined);
    
    return cards;
  }

  /**
   * Clear the registry (for testing purposes)
   */
  clear() {
    logger.log("[CardRegistry] Clearing registry - current cards:", Object.keys(this.cards));
    this.cards = {};
    this.cardMetadata = {};
    this.roleCards = {};
    this.initialized = false;
  }
}

// Create and export a singleton instance
export const cardRegistry = new CardRegistry();

// Export the class for testing purposes
export default CardRegistry;